import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { capitalizeFirstLetter } from "../../../utils/capitalize";

// ============================================
// Styles
// ============================================

const textFieldSx = (disabled) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F2F2F2" : "#FFFFFF",
    borderRadius: "8px",
    height: "43px",
    "& fieldset": { border: disabled ? "none" : "1px solid #E8E8E9" }
  },
  "& .MuiInputBase-input": { 
    fontSize: "14px", 
    color: disabled ? "#999893" : "#0e1324", 
    pr: 0 
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#999893",
    fontSize: "14px"
  }
});

const labelSx = {
  fontSize: "13px",
  color: "#66638C",
  fontWeight: 500,
  mb: "8px",
  display: "block"
};

// ============================================
// Helper functions
// ============================================

const isDisabled = (copayDisabled, disabled) => {
  return copayDisabled || disabled;
};

const getCharacterLimit = (field, copayData) => {
  const limits = {
    iinNumber: 8,
    processorIdentificationNumber: 15,
    groupID: 20,
    cardHolderID: 20,
    code: 10,
    description: 100
  };
  return limits[field] || 0;
};

const getFieldValue = (data, field, disabled, defaultValue = "") => {
  if (disabled && !data[field]) return "-";
  return data[field] || defaultValue;
};

const getDisplayValue = (data, field, disabled) => {
  if (disabled) {
    if (field === "copayAssistanceAmount") {
      const value = data[field];
      if (value !== undefined && value !== "" && value !== 0) return value;
      return "-";
    }
    if (field === "description") {
      return data[field] ? capitalizeFirstLetter(data[field]) : "-";
    }
    if (field === "code") {
      return data[field] || "-";
    }
  }
  return data[field] || "";
};

const getInputType = (field, data, disabled) => {
  if (field === "copayAssistanceAmount") {
    if (disabled && (data[field] === undefined || data[field] === "" || data[field] === 0)) {
      return "text";
    }
    return "number";
  }
  return "text";
};

// ============================================
// Reusable TextField component with character counter
// ============================================

const CharacterLimitedTextField = ({
  label,
  placeholder,
  value,
  onChange,
  disabled,
  maxLength,
  slotProps,
  sx,
  type = "text",
  ...props
}) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  const showCounter = !disabled && maxLength > 0;

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0 }}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        type={type}
        onChange={handleChange}
        slotProps={showCounter ? {
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Typography component="span" sx={{ fontSize: "12px", color: "#666", whiteSpace: "nowrap", mr: 0.5 }}>
                  {(value || "").length}/{maxLength}
                </Typography>
              </InputAdornment>
            ),
          },
        } : undefined}
        sx={sx}
        {...props}
      />
    </Box>
  );
};

CharacterLimitedTextField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number.isRequired,
  slotProps: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  type: PropTypes.string,
};

// ============================================
// Main Component
// ============================================

const CopayAssistanceAndDiagnosis = ({ 
  copayData, 
  diagnosisData, 
  onCopayChange, 
  onDiagnosisChange, 
  copayDisabled, 
  disabled 
}) => {
  const copayIsDisabled = isDisabled(copayDisabled, disabled);
  const allDisabled = isDisabled(true, disabled);

  // Copay field handlers
  const handleCopayChange = (field) => (value) => {
    onCopayChange(field, value);
  };

  // Diagnosis field handlers
  const handleDiagnosisChange = (field) => (value) => {
    onDiagnosisChange(field, value);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Copay Assistance Card Section */}
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mb: 1 }}>
        Copay Card
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <CharacterLimitedTextField
          label="IIN Number (RxBIN)"
          placeholder="Enter RxBIN"
          value={copayData.iinNumber || ""}
          onChange={handleCopayChange("iinNumber")}
          disabled={copayIsDisabled}
          maxLength={8}
          sx={textFieldSx(copayIsDisabled)}
        />
        <CharacterLimitedTextField
          label="Processor ID (RxPCN)"
          placeholder="Enter RxPCN"
          value={copayData.processorIdentificationNumber || ""}
          onChange={handleCopayChange("processorIdentificationNumber")}
          disabled={copayIsDisabled}
          maxLength={15}
          sx={textFieldSx(copayIsDisabled)}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <CharacterLimitedTextField
          label="Group ID (RxGRP)"
          placeholder="Enter RxGRP"
          value={getFieldValue(copayData, "groupID", copayIsDisabled)}
          onChange={handleCopayChange("groupID")}
          disabled={copayIsDisabled}
          maxLength={20}
          sx={textFieldSx(copayIsDisabled)}
        />
        <CharacterLimitedTextField
          label="Card Holder ID"
          placeholder="Enter Card Holder ID"
          value={getFieldValue(copayData, "cardHolderID", copayIsDisabled)}
          onChange={handleCopayChange("cardHolderID")}
          disabled={copayIsDisabled}
          maxLength={20}
          sx={textFieldSx(copayIsDisabled)}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={labelSx}>Copay Assistance Amount ($)</Typography>
        <TextField
          fullWidth
          type={getInputType("copayAssistanceAmount", copayData, copayIsDisabled)}
          size="small"
          placeholder="Enter Copay Assistance Amount"
          value={getDisplayValue(copayData, "copayAssistanceAmount", copayIsDisabled)}
          disabled={copayIsDisabled}
          onChange={(e) => handleCopayChange("copayAssistanceAmount")(e.target.value)}
          sx={textFieldSx(copayIsDisabled)}
        />
      </Box>

      {/* Diagnosis Section */}
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mt: 2, mb: 1 }}>
        Diagnosis
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <CharacterLimitedTextField
          label="ICD Code"
          placeholder="Enter ICD Code"
          value={getDisplayValue(diagnosisData, "code", allDisabled)}
          onChange={handleDiagnosisChange("code")}
          disabled={allDisabled}
          maxLength={10}
          sx={textFieldSx(allDisabled)}
        />
        <CharacterLimitedTextField
          label="Diagnosis Description"
          placeholder="Enter Diagnosis Description"
          value={getDisplayValue(diagnosisData, "description", allDisabled)}
          onChange={handleDiagnosisChange("description")}
          disabled={allDisabled}
          maxLength={100}
          sx={textFieldSx(allDisabled)}
        />
      </Box>
    </Box>
  );
};

CopayAssistanceAndDiagnosis.propTypes = {
  copayData: PropTypes.object.isRequired,
  diagnosisData: PropTypes.object.isRequired,
  onCopayChange: PropTypes.func.isRequired,
  onDiagnosisChange: PropTypes.func.isRequired,
  copayDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
};

CopayAssistanceAndDiagnosis.defaultProps = {
  copayDisabled: false,
  disabled: false,
};

export default CopayAssistanceAndDiagnosis;